import React, { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Organization } from "../common/ObjectTypes";
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import { SearchIcon } from "../common/ProjectIcons";
import { page_size } from "../../api/apiUtils";
import { containerClasses, textClasses, tableClasses, buttonClasses, paginationClasses, skeletonClasses } from "../common/tailwindClassnames";

type OrganizationsListProps = {
    organizations: Array<Organization>,
    onChangeSearchString: Function,
    onSelectOrg: Function,
    onAddOrg: MouseEventHandler,
    superuser: boolean,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    searchString: string,
    onSearch: (term: string) => void
}

const OrganizationsList = ({
    organizations,
    onChangeSearchString,
    onSelectOrg,
    onAddOrg,
    superuser,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    searchString,
    onSearch
}: OrganizationsListProps) => {
    const { t } = useTranslation();

    const handleSearchClick = () => {
        onSearch(searchString);
    };

    return (
        <div className={containerClasses.page}>
            <div className="mb-12 flex justify-between max-w-list-table mx-auto">
                <h1 data-testid="organizations_page_header" className={textClasses.heading}>{t("Workspaces")}</h1>
                {superuser && <button className={buttonClasses.primary}
                    onClick={onAddOrg}>
                    {t("Create Workspace")}
                </button>}
            </div>
            <div className="flex flex-col w-full max-w-list-table mx-auto">
                <div className="flex flex-row items-end gap-3 mb-3">
                    <div className="flex flex-row items-center mb-2">
                        <input
                            id="name"
                            placeholder={t("Search by name") || ""}
                            className="p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 text-off-black transition-all duration-300"
                            value={searchString}
                            onChange={(e) => { onChangeSearchString(e) }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearchClick();
                                }
                            }}>
                        </input>
                        <button className="mx-2 bg-dark-green hover:bg-status-green text-white rounded text-sm p-2 h-[34px] self-center transition-colors duration-300 mb-0"
                            type="button"
                            onClick={handleSearchClick}
                        >
                            <SearchIcon />
                        </button>
                    </div>
                </div>
                <table className={tableClasses.container + " table-fixed"}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={tableClasses.headerCell + " w-1/3"}>
                                {t("Name")}
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Description")}
                            </th>
                        </tr>
                    </thead>
                    {!pageLoaded ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className="border border-gray-lines bg-white" data-testid="loading">
                                    <td className="p-3 w-1/3"><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className="p-3"><div className={`h-4 w-64 ${skeletonClasses.base}`} /></td>
                                </tr>
                            ))}
                        </tbody>
                        :
                        <tbody className="text-sm transition-colors duration-300">
                            {organizations.map((org: Organization) => {
                                return (
                                    <tr key={org.id}
                                        className="border border-gray-lines hover:bg-light-blue bg-white transition-colors duration-300"
                                        onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/workspaces/${org.id}`, '_blank') : onSelectOrg(org.id)}
                                    >
                                        <td className="p-3 w-1/3 transition-colors duration-300 cursor-pointer">
                                            {org.name}
                                        </td>
                                        <td className="p-3 transition-colors duration-300">
                                            <p className="break-words">{org.description}</p>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>}
                    {pageLoaded && organizations.length === 0 &&
                        <tbody><tr><td colSpan={2} className="py-16">
                            <NoResults
                                headerMsg={t("No Results Found")}
                                labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                            />
                        </td></tr></tbody>}
                </table>
                {totalCount > page_size && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
        </div>
    );
};

export default OrganizationsList;